import type { Cenario, DistribuicaoMensal, Parametros } from '@favo/core';

import { Indicador } from '@/componentes/indicador';
import { Valor } from '@/componentes/valor';
import { formatarMoeda, formatarPercentual, formatarPercentualPontos } from '@/design/formato';

import { faltaPreencher, notaDeVolume, notaEquilibrio, textoEquilibrio } from './frases';

/** Os quatro números do ano: faturamento, margem de contribuição, lucro, equilíbrio. */
export function SecaoIndicadores({
  p,
  atual,
  dist,
}: {
  p: Parametros;
  atual: Cenario | null;
  dist: DistribuicaoMensal | null;
}) {
  const falta = faltaPreencher(p);
  const lucroAno = atual?.anual.lucro ?? null;
  const negativo = lucroAno !== null && lucroAno < 0;

  // Escala da régua: o dobro da meta, para que bater a meta caia no meio e
  // superá-la ainda tenha para onde crescer.
  const alvoFracao = p.lucroAlvo / 100;
  const escala = Math.max(alvoFracao * 2, atual?.lucroPct ?? 0, 0.01);

  return (
    <section className="secao">
      <h2 className="titulo-cartao titulo-secao">Indicadores do ano</h2>
      <div className="favo grade-indicadores">
        <Indicador rotulo="Faturamento anual" campo="areia" apoio={atual ? notaDeVolume(p) : falta}>
          <Valor
            valor={atual?.anual.faturamento ?? null}
            formato="moeda"
            tamanho="secao"
            className="whitespace-nowrap"
          />
        </Indicador>

        <Indicador
          rotulo="Margem de contribuição"
          campo="mint"
          apoio={
            atual
              ? `${formatarMoeda(atual.mcUnit)} por venda para cobrir a despesa fixa. Só depois disso começa o lucro.`
              : falta
          }
        >
          <Valor
            valor={atual?.mcPct ?? null}
            formato="percentual"
            tamanho="secao"
            className="whitespace-nowrap"
            tom={atual && atual.mcUnit < 0 ? 'negativo' : 'neutro'}
          />
        </Indicador>

        <Indicador
          rotulo={negativo ? 'Prejuízo anual' : 'Lucro anual'}
          campo={negativo ? 'telha' : 'nata'}
          apoio={
            lucroAno === null
              ? falta
              : `${formatarPercentual(atual?.lucroPct ?? null)} do faturamento.`
          }
          {...(atual && atual.lucroPct !== null
            ? {
                regua: {
                  fracao: atual.lucroPct / escala,
                  meta: alvoFracao / escala,
                  tom: negativo ? ('negativo' as const) : ('positivo' as const),
                },
                referencia: `meta ${formatarPercentualPontos(p.lucroAlvo)}`,
              }
            : {})}
        >
          <Valor
            valor={lucroAno === null ? null : Math.abs(lucroAno)}
            formato="moeda"
            tamanho="secao"
            className="whitespace-nowrap"
            tom={lucroAno === null ? 'neutro' : negativo ? 'negativo' : 'positivo'}
          />
        </Indicador>

        <Indicador
          rotulo={dist?.uniforme ? 'Equilíbrio estimado' : 'Equilíbrio financeiro'}
          campo="mel"
          apoio={notaEquilibrio(p, dist, falta)}
        >
          <span className="cifra text-secao">{textoEquilibrio(dist)}</span>
        </Indicador>
      </div>
    </section>
  );
}
